import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Alert,
  Tooltip,
  useTheme,
  alpha,
  Stack,
  Fab,
  Zoom,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  DeleteSweep as BulkDeleteIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useDoctorShiftList } from '../hooks/useDoctorShiftList';
import { useNotification } from '../hooks/useNotification';
import { DoctorShiftTable } from '../components/DoctorShift/DoctorShiftTable';
import { DoctorShiftEditModal } from '../components/DoctorShift/DoctorShiftEditModal';
import { DoctorShiftDetailModal } from '../components/DoctorShift/DoctorShiftDetailModal';
import { DoctorShiftDeleteDialog } from '../components/DoctorShift/DoctorShiftDeleteDialog';
import { DoctorShiftFilterDrawer } from '../components/DoctorShift/DoctorShiftFilterDrawer';

/**
 * 医師シフト管理ページ
 * 
 * 意図：
 * - 医師シフトデータの一覧表示と管理
 * - 高度な検索・フィルタリング機能の提供
 * - CRUD操作の統合UI
 * - CSVアップロードページへの導線
 * 
 * 注意点：
 * - パフォーマンス最適化
 * - エラーハンドリング
 * - レスポンシブ対応
 * 
 * 改善提案：
 * - リアルタイム更新機能
 * - エクスポート機能
 * - 一括操作の拡張
 */
export const DoctorShiftListPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  // 医師シフト管理フック
  const {
    data,
    meta,
    isLoading,
    error,
    filters,
    selectedShift,
    editModalOpen,
    detailModalOpen,
    deleteDialogOpen,
    updateFilters,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleSearch,
    refresh,
    showDetails,
    showEditModal,
    showDeleteDialog,
    closeModals,
    createShift,
    updateShift,
    deleteShift,
    bulkDeleteShifts,
    clearError,
  } = useDoctorShiftList();

  // ローカル状態
  const [searchQuery, setSearchQuery] = useState(filters.search || '');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // 検索実行
  const handleSearchSubmit = () => {
    handleSearch(searchQuery);
  };

  // 検索フィールドでのEnterキー処理
  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // CSVアップロードページへの遷移
  const handleNavigateToUpload = () => {
    navigate('/doctors/shift-upload');
  };

  // 新規作成
  const handleCreate = () => {
    showEditModal();
  };

  // 編集
  const handleEdit = (shift: any) => {
    showEditModal(shift);
  };

  // 編集・新規作成の保存
  const handleSave = async (data: any) => {
    try {
      const success = selectedShift 
        ? await updateShift(selectedShift.id, data)
        : await createShift(data);
      
      if (success) {
        showNotification(
          'success',
          selectedShift ? 'シフトを更新しました' : 'シフトを作成しました'
        );
      }
      return success;
    } catch (error) {
      showNotification('error', '操作に失敗しました');
      return false;
    }
  };

  // 削除
  const handleDelete = async () => {
    if (!selectedShift) return false;
    
    try {
      const success = await deleteShift(selectedShift.id);
      if (success) {
        showNotification('success', 'シフトを削除しました');
      }
      return success;
    } catch (error) {
      showNotification('error', '削除に失敗しました');
      return false;
    }
  };

  // 一括削除
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      const success = await bulkDeleteShifts(selectedIds);
      if (success) {
        showNotification('success', `${selectedIds.length}件のシフトを削除しました`);
        setSelectedIds([]);
      }
    } catch (error) {
      showNotification('error', '一括削除に失敗しました');
    }
  };

  // フィルター適用
  const handleFilterApply = () => {
    refresh();
  };

  // フィルターリセット
  const handleFilterReset = () => {
    updateFilters({
      search: undefined,
      start_date: undefined,
      end_date: undefined,
      time_range_start: undefined,
      time_range_end: undefined,
      doctor_ids: undefined,
      page: 1,
    });
    setSearchQuery('');
  };

  // 詳細表示から編集への遷移
  const handleDetailToEdit = () => {
    closeModals();
    if (selectedShift) {
      showEditModal(selectedShift);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* ヘッダーセクション */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            医師シフト管理
          </Typography>
          <Typography variant="body1" color="text.secondary">
            医師のシフト情報を管理します。検索・フィルタリング機能を使用して効率的にデータを確認できます。
          </Typography>
        </Box>
      </motion.div>

      {/* エラー表示 */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{ marginBottom: 24 }}
          >
            <Alert
              severity="error"
              onClose={clearError}
              sx={{ borderRadius: 2 }}
            >
              {error}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 操作パネル */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            {/* 検索バー */}
            <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: 400 } }}>
              <TextField
                fullWidth
                placeholder="医師IDで検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleSearchSubmit}
                        disabled={isLoading}
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 }
                }}
              />
            </Box>

            {/* アクションボタン */}
            <Stack direction="row" spacing={1}>
              {/* 一括削除ボタン */}
              {selectedIds.length > 0 && (
                <Tooltip title={`選択中の${selectedIds.length}件を削除`}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<BulkDeleteIcon />}
                    onClick={handleBulkDelete}
                    disabled={isLoading}
                    sx={{ borderRadius: 2 }}
                  >
                    削除 ({selectedIds.length})
                  </Button>
                </Tooltip>
              )}

              {/* フィルターボタン */}
              <Tooltip title="フィルター">
                <IconButton
                  onClick={() => setFilterDrawerOpen(true)}
                  sx={{
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <FilterIcon />
                </IconButton>
              </Tooltip>

              {/* リフレッシュボタン */}
              <Tooltip title="リフレッシュ">
                <IconButton
                  onClick={refresh}
                  disabled={isLoading}
                  sx={{
                    color: theme.palette.success.main,
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.success.main, 0.2),
                    },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              {/* CSVアップロードボタン */}
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={handleNavigateToUpload}
                sx={{ borderRadius: 2 }}
              >
                CSVアップロード
              </Button>

              {/* 新規作成ボタン */}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreate}
                sx={{ borderRadius: 2 }}
              >
                新規作成
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </motion.div>

      {/* データテーブル */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <DoctorShiftTable
          data={data}
          isLoading={isLoading}
          onViewDetails={showDetails}
          onEdit={handleEdit}
          onDelete={showDeleteDialog}
          filters={filters}
          onFiltersChange={updateFilters}
          meta={meta || undefined}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSort}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </motion.div>

      {/* フローティングアクションボタン（モバイル用） */}
      <Zoom in={true}>
        <Fab
          color="primary"
          onClick={handleCreate}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            display: { xs: 'flex', md: 'none' },
          }}
        >
          <AddIcon />
        </Fab>
      </Zoom>

      {/* モーダル・ダイアログ */}
      <DoctorShiftEditModal
        open={editModalOpen}
        onClose={closeModals}
        onSave={handleSave}
        shift={selectedShift}
        isLoading={isLoading}
      />

      <DoctorShiftDetailModal
        open={detailModalOpen}
        onClose={closeModals}
        onEdit={handleDetailToEdit}
        shift={selectedShift}
      />

      <DoctorShiftDeleteDialog
        open={deleteDialogOpen}
        onClose={closeModals}
        onConfirm={handleDelete}
        shift={selectedShift}
        isLoading={isLoading}
      />

      <DoctorShiftFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={filters}
        onFiltersChange={updateFilters}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
      />
    </Box>
  );
};